import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUp } from 'lucide-react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import { AnimatedSuggestionPlaceholder } from 'src/shared/components/GlobalChatWidget/components/AnimatedSuggestionPlaceholder'
import { PoweredByChatGPT } from 'src/shared/components/GlobalChatWidget/components/PoweredByChatGPT'
import { SaraMentionHighlight } from 'src/shared/components/GlobalChatWidget/components/SaraMentionHighlight'
import {
  SARA_PREFILL_TEXT,
  isSaraPrefillOnly,
} from 'src/shared/components/GlobalChatWidget/components/saraPrefill'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import {
  SARA_KIT_PROMPTS,
  SARA_PRODUCT_PROMPTS,
  SARA_SUGGESTION_CYCLE_MS,
} from './consts'

const EMPTY_EDITOR_CONTENT = {
  type: 'doc' as const,
  content: [{ type: 'paragraph' }],
}

const SARA_EDITOR_CONTENT = {
  type: 'doc' as const,
  content: [
    {
      type: 'paragraph',
      content: [{ type: 'text', text: SARA_PREFILL_TEXT }],
    },
  ],
}

const SaraTabHintPlaceholder = ({ afterTab }: { afterTab: string }) => (
  <span
    className="pointer-events-none absolute left-5 right-3 top-2 z-[2] flex flex-wrap items-center gap-x-1.5 gap-y-1 text-base leading-normal text-muted-foreground/55"
    aria-hidden
  >
    <span className="md:hidden">Tab</span>
    <kbd className="hidden h-[18px] min-w-[28px] items-center justify-center rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70 shadow-[0_1px_0_rgba(0,0,0,0.04)] md:inline-flex">
      tab
    </kbd>
    <span>{afterTab}</span>
  </span>
)

const isSendable = (text: string) =>
  !!text.trim() && !isSaraPrefillOnly(text)

const shuffle = <T,>(items: T[]): T[] => {
  const next = [...items]
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[next[i], next[j]] = [next[j], next[i]]
  }
  return next
}

const interleavedSuggestions = () => {
  const products = shuffle(SARA_PRODUCT_PROMPTS)
  const kits = shuffle(SARA_KIT_PROMPTS)
  const count = Math.max(products.length, kits.length)
  const mixed: typeof products = []

  for (let i = 0; i < count; i++) {
    mixed.push(products[i % products.length])
    mixed.push(kits[i % kits.length])
  }

  return mixed
}

export const SaraPromptInput = ({
  onSubmit,
  isSubmitting,
  startEmpty = false,
  showPoweredBy = false,
}: {
  onSubmit: (message: string) => Promise<void>
  isSubmitting: boolean
  startEmpty?: boolean
  showPoweredBy?: boolean
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const [editorText, setEditorText] = useState(
    startEmpty ? '' : SARA_PREFILL_TEXT
  )
  const [suggestionIndex, setSuggestionIndex] = useState(0)
  const [suggestionOffset, setSuggestionOffset] = useState({ left: 0, top: 0 })
  const handleSubmitRef = useRef<() => void>(() => {})
  const acceptSuggestionRef = useRef<() => boolean>(() => false)
  const insertSaraMentionRef = useRef<() => boolean>(() => false)
  const editorAreaRef = useRef<HTMLDivElement>(null)
  const measureSuggestionOffsetRef = useRef<() => void>(() => {})
  const [prompts] = useState(interleavedSuggestions)

  const suggestions = useMemo(
    () => prompts.map((prompt) => t(prompt.en, prompt.sv)),
    [prompts, t]
  )

  const canSend = isSendable(editorText)
  const showTabHint = startEmpty && !editorText.trim()
  const showSuggestions = isSaraPrefillOnly(editorText)
  const activeSuggestion =
    suggestions[suggestionIndex % suggestions.length] ?? ''

  const placeholder =
    showSuggestions || showTabHint
      ? ''
      : t('What would you like to order today?', 'Vad vill du beställa idag?')

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder }),
      SaraMentionHighlight,
    ],
    [placeholder]
  )

  const editor = useEditor({
    extensions,
    content: startEmpty ? EMPTY_EDITOR_CONTENT : SARA_EDITOR_CONTENT,
    autofocus: false,
    editorProps: {
      attributes: {
        class: 'outline-none',
        ...(startEmpty
          ? {
              'aria-label': t(
                'Tab to ask Sara or write to the team to start a new request',
                'Tab för att be Sara eller skriva till teamet och starta en ny förfrågan'
              ),
            }
          : {}),
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          handleSubmitRef.current()
          return true
        }
        if (event.key === 'Tab' && !event.shiftKey) {
          if (acceptSuggestionRef.current() || insertSaraMentionRef.current()) {
            event.preventDefault()
            return true
          }
        }
      },
    },
    editable: !isSubmitting,
    onUpdate: ({ editor: activeEditor }) => {
      setEditorText(activeEditor.getText())
      requestAnimationFrame(() => {
        measureSuggestionOffsetRef.current()
      })
    },
  })

  const measureSuggestionOffset = useCallback(() => {
    if (!editorAreaRef.current) return

    const mention = editorAreaRef.current.querySelector(
      '.sara-mention-highlight'
    ) as HTMLElement | null
    if (!mention) return

    const mentionRect = mention.getBoundingClientRect()
    const areaRect = editorAreaRef.current.getBoundingClientRect()

    setSuggestionOffset({
      left: mentionRect.right - areaRect.left + 4,
      top: mentionRect.top - areaRect.top,
    })
  }, [])

  const restoreComposer = useCallback(() => {
    if (!editor || editor.isDestroyed) return
    if (startEmpty) {
      editor.chain().setContent(EMPTY_EDITOR_CONTENT).focus('end').run()
      setEditorText('')
      return
    }
    editor.chain().setContent(SARA_EDITOR_CONTENT).focus('end').run()
    setEditorText(SARA_PREFILL_TEXT)
  }, [editor, startEmpty])

  const insertSaraMention = useCallback(() => {
    if (!editor || editor.isDestroyed || isSubmitting || !startEmpty) {
      return false
    }
    if (editorText.trim()) return false

    editor.chain().setContent(SARA_EDITOR_CONTENT).focus('end').run()
    setEditorText(SARA_PREFILL_TEXT)
    return true
  }, [editor, editorText, isSubmitting, startEmpty])

  const acceptSuggestion = useCallback(() => {
    if (!editor || editor.isDestroyed || isSubmitting) return false
    if (!showSuggestions || !activeSuggestion) return false

    const nextText = `${SARA_PREFILL_TEXT}${activeSuggestion} `
    editor
      .chain()
      .setContent({
        type: 'doc',
        content: [
          {
            type: 'paragraph',
            content: [{ type: 'text', text: nextText }],
          },
        ],
      })
      .focus('end')
      .run()
    setEditorText(nextText)
    return true
  }, [activeSuggestion, editor, isSubmitting, showSuggestions])

  const handleSubmit = useCallback(async () => {
    if (!editor || isSubmitting) return

    const text = editor.getText()
    if (!isSendable(text)) return

    try {
      await onSubmit(text.trim())
      restoreComposer()
    } catch (error) {
      console.error('Sara prompt submit error:', error)
      toast({
        variant: 'destructive',
        title: t('Failed', 'Misslyckades'),
        description:
          error instanceof Error
            ? error.message
            : t(
                'Unable to create order. Please try again.',
                'Kunde inte skapa order. Försök igen.'
              ),
      })
    }
  }, [editor, isSubmitting, onSubmit, restoreComposer, t, toast])

  useEffect(() => {
    handleSubmitRef.current = handleSubmit
  }, [handleSubmit])

  useEffect(() => {
    acceptSuggestionRef.current = acceptSuggestion
  }, [acceptSuggestion])

  useEffect(() => {
    insertSaraMentionRef.current = insertSaraMention
  }, [insertSaraMention])

  useEffect(() => {
    measureSuggestionOffsetRef.current = measureSuggestionOffset
  }, [measureSuggestionOffset])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.setEditable(!isSubmitting)
  }, [editor, isSubmitting])

  useEffect(() => {
    if (!showSuggestions) {
      setSuggestionOffset({ left: 0, top: 0 })
      return
    }

    measureSuggestionOffset()
    let raf2 = 0
    const raf = requestAnimationFrame(() => {
      measureSuggestionOffset()
      raf2 = requestAnimationFrame(measureSuggestionOffset)
    })

    const mention = editorAreaRef.current?.querySelector(
      '.sara-mention-highlight'
    )
    const observer =
      mention && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measureSuggestionOffset)
        : null
    if (mention && observer) observer.observe(mention)

    return () => {
      cancelAnimationFrame(raf)
      cancelAnimationFrame(raf2)
      observer?.disconnect()
    }
  }, [editor, editorText, measureSuggestionOffset, showSuggestions, suggestionIndex])

  useEffect(() => {
    if (!showSuggestions || suggestions.length < 2) return

    const id = window.setInterval(() => {
      setSuggestionIndex((index) => (index + 1) % suggestions.length)
    }, SARA_SUGGESTION_CYCLE_MS)

    return () => window.clearInterval(id)
  }, [showSuggestions, suggestions.length])

  if (!editor) return null

  const focusEditor = () => {
    if (editor.isDestroyed || isSubmitting) return
    editor.commands.focus('end')
  }

  return (
    <Column
      className={cn(
        'w-full cursor-text gap-0 rounded-2xl border border-white/60 bg-white/70 text-left shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_12px_32px_hsl(220_20%_12%/0.08)] backdrop-blur-xl transition-shadow focus-within:bg-white/85 focus-within:shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_16px_40px_hsl(220_20%_12%/0.12)]',
        showPoweredBy ? 'px-2 pb-2 pt-2' : 'p-2'
      )}
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('button')) return
        focusEditor()
      }}
    >
      <div
        ref={editorAreaRef}
        className="relative min-h-[56px] w-full overflow-hidden px-3 py-2 text-base [&_.sara-mention-highlight]:inline-block [&_.sara-mention-highlight]:rounded-md [&_.sara-mention-highlight]:bg-violet-50 [&_.sara-mention-highlight]:px-1.5 [&_.sara-mention-highlight]:py-1 [&_.sara-mention-highlight]:align-middle [&_.sara-mention-highlight]:font-semibold [&_.sara-mention-highlight]:leading-none [&_.sara-mention-highlight]:text-violet-700 [&_.sara-mention-highlight]:shadow-[inset_0_0_0_1px_rgba(124,58,237,0.15)] [&_.sara-mention-highlight]:[box-decoration-break:clone] [&_.tiptap]:min-h-[40px] [&_.tiptap]:outline-none [&_.tiptap_p]:m-0 [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]"
      >
        <EditorContent editor={editor} />
        {showTabHint ? (
          <SaraTabHintPlaceholder
            afterTab={t(
              'to ask Sara or write to the team to start a new request',
              'för att be Sara eller skriva till teamet och starta en ny förfrågan'
            )}
          />
        ) : null}
        {showSuggestions && activeSuggestion && suggestionOffset.left > 0 ? (
          <button
            type="button"
            className="absolute z-[2] max-w-[calc(100%-0.5rem)] cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap text-left text-base leading-normal"
            style={{
              left: suggestionOffset.left,
              top: suggestionOffset.top,
              maxWidth: `calc(100% - ${suggestionOffset.left}px - 8px)`,
            }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={acceptSuggestion}
            aria-label={t(
              `Use suggestion: ${activeSuggestion}`,
              `Använd förslag: ${activeSuggestion}`
            )}
          >
            <AnimatedSuggestionPlaceholder text={activeSuggestion} />
          </button>
        ) : null}
      </div>

      <Row
        className={
          showPoweredBy
            ? 'items-center justify-between gap-2 px-3 pb-0.5'
            : 'items-center justify-end px-2 pb-1'
        }
      >
        {showPoweredBy ? <PoweredByChatGPT /> : null}
        <Button
          variant={canSend ? 'primary' : 'ghost'}
          size="sm"
          disabled={!canSend || isSubmitting}
          isLoading={isSubmitting}
          onClick={handleSubmit}
          className={
            canSend
              ? 'gap-1.5 rounded-full px-3'
              : 'gap-1.5 rounded-full border border-border px-3 text-muted-foreground'
          }
        >
          {t('New request', 'Ny förfrågan')}
          <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
        </Button>
      </Row>

      {showSuggestions && activeSuggestion ? (
        <button
          type="button"
          className="mx-2 mb-1 max-w-[calc(100%-1rem)] truncate rounded-full border border-violet-200/80 bg-violet-50 px-3 py-1.5 text-left text-sm text-violet-800 md:hidden"
          onMouseDown={(event) => event.preventDefault()}
          onClick={acceptSuggestion}
        >
          {activeSuggestion}
        </button>
      ) : null}
    </Column>
  )
}
